// Working out what a data item's underlying media actually is, so it can be previewed.
//
// Media items carry `media_type` of "file" | "text" | "url" (the *source* of the item), not the
// file kind, so the kind has to come from the file extension. A media URL may live in any of
// several fields depending on how the item was created.

const VIDEO_EXTENSIONS = ["mp4", "m4v", "mov", "webm", "ogv", "mkv", "m3u8"];
const AUDIO_EXTENSIONS = ["mp3", "wav", "m4a", "aac", "ogg", "oga", "flac", "wma"];
const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "avif"];
const OFFICE_EXTENSIONS = ["doc", "docx", "ppt", "pptx", "xls", "xlsx", "odt", "odp", "ods"];
const TEXT_EXTENSIONS = ["txt", "md", "markdown", "csv", "json", "log", "xml", "html", "htm"];

const firstNonEmpty = (...values) =>
  values.find((value) => typeof value === "string" && value.trim() !== "") ?? null;

/** The file URL for a media/data item, whichever field it happens to be in. */
export const getMediaUrl = (item) =>
  firstNonEmpty(
    item?.media_url,
    item?.fullpath,
    item?.url,
    item?.file_url,
    item?.filepath,
    item?.path
  );

/** Inline text body, for items that are text rather than a file. */
export const getMediaText = (item) =>
  firstNonEmpty(item?.text, item?.text_content, item?.content, item?.body);

export const getFileExtension = (url) => {
  if (!url) return "";
  const withoutQuery = String(url).split(/[?#]/)[0];
  const lastSegment = withoutQuery.split("/").pop() ?? "";
  const dot = lastSegment.lastIndexOf(".");
  return dot === -1 ? "" : lastSegment.slice(dot + 1).toLowerCase();
};

export const isHlsStream = (url) => /\.m3u8$/i.test(String(url ?? "").split(/[?#]/)[0]);

/**
 * Classify how a media item can be previewed.
 *
 * @returns {"video"|"audio"|"image"|"pdf"|"office"|"document"|"text"|"link"|"none"}
 */
export const getPreviewKind = (item) => {
  const url = getMediaUrl(item);
  const extension = getFileExtension(url);

  if (VIDEO_EXTENSIONS.includes(extension)) return "video";
  if (AUDIO_EXTENSIONS.includes(extension)) return "audio";
  if (IMAGE_EXTENSIONS.includes(extension)) return "image";
  if (extension === "pdf") return "pdf";
  if (OFFICE_EXTENSIONS.includes(extension)) return "office";
  if (TEXT_EXTENSIONS.includes(extension)) return "document";

  // No extension to go on: fall back to how the item was created.
  if (getMediaText(item)) return "text";
  if (item?.media_type === "text") return "text";

  return url ? "link" : "none";
};

/** A short, human label for the kind of file ("PDF", "MP4", "Document", …). */
export const getPreviewLabel = (item) => {
  const kind = getPreviewKind(item);
  if (kind === "link") {
    return getFileExtension(getMediaUrl(item)).toUpperCase() || "Link";
  }
  const labels = {
    video: "Video",
    audio: "Audio",
    image: "Image",
    pdf: "PDF",
    office: "Document",
    document: "Text file",
    text: "Text",
    none: "Unavailable",
  };
  return labels[kind] ?? "Unknown";
};

/**
 * Microsoft's online viewer can render Office documents the browser cannot, but only if the
 * URL is publicly reachable.
 */
export const getOfficeViewerUrl = (url) =>
  url ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}` : null;
